/**
 * ENG-6157 (Phase 2) — live read-only tool call through the agent's wired
 * Composio MCP server.
 *
 * Phase 1 (`assessAuthConfigLinkage`) deterministically catches the
 * auth_config/server mismatch from metadata. This is the belt-and-suspenders
 * leg the issue's fix-option-1 describes: actually exercise the agent's *wired
 * MCP URL* with a real tool call — the ONLY call that proves the server
 * resolves the connected account end-to-end. `tools/list` is green even when no
 * account resolves; only `tools/call` surfaces `No connected account found …`.
 *
 * Safety: we never hardcode tool slugs (they drift per toolkit/version). Instead
 * we read the server's own `tools/list` and pick a tool that is provably
 * side-effect-free: its name carries a read-only verb (LIST/GET/SEARCH/…) AND
 * its input schema has no required parameters, so calling it with `{}` cannot
 * mutate anything. If no such tool exists we return `null` (skip) — Phase 1
 * still governs. A wrong guess therefore degrades to a no-op, never a false
 * result and never a side effect.
 *
 * Outcome:
 *   - `null`             — no safe tool to call (skip; not a verdict)
 *   - `'ok'`             — the call resolved the connected account (success, or
 *                          a benign non-account error like arg validation)
 *   - `'down'`           — either the wired server couldn't resolve the account
 *                          (account-resolution error), OR (ENG-6328) it resolved
 *                          the account but the upstream provider rejected its
 *                          credential (401/403 / "authentication required" /
 *                          `successful:false` envelope) → reconnect required
 *   - `'transient_error'`— transport/timeout/5xx (retryable)
 *
 * `fetch` is injected for unit-testing. Streamable-HTTP MCP only (Composio).
 */

import type { ConnectivityProbeOutcome } from './connectivity-probe.js';

const MCP_ACCEPT = 'application/json, text/event-stream';
const DEFAULT_TIMEOUT_MS = 10_000;

/** Verb tokens that mark a Composio tool as read-only (no mutation). */
export const READONLY_VERB_TOKENS = [
  'LIST',
  'GET',
  'FIND',
  'SEARCH',
  'FETCH',
  'COUNT',
  'RETRIEVE',
  'READ',
] as const;

/** Composio error fragments that mean "the wired server couldn't resolve the account". */
const ACCOUNT_RESOLUTION_ERROR_PATTERNS = [
  'no connected account',
  'connected account not found',
  'no account found',
  'could not be resolved',
  'auth config',
  'no connection found',
] as const;

/**
 * ENG-6328 — phrases that mean the wired server RESOLVED the connected account
 * but the UPSTREAM provider rejected its credential (a dead/revoked OAuth grant,
 * an expired token, a 401/403). This is distinct from an account-resolution
 * failure (the Composio-side miss above) and from a benign tool error (bad
 * args): it's the "integration is broken, reconnect required" signal that
 * Composio's own record hides — it keeps the account ACTIVE with
 * `auth_refresh_required:false`, so only a live call surfaces it (the sherlock
 * incident: every Linear call 401'd while the console Test stayed green).
 */
const UPSTREAM_AUTH_ERROR_PATTERNS = [
  'authentication required',
  'not authenticated',
  'unauthorized',
  'authentication_error',
  'authentication error',
  'invalid authentication',
  'invalid credentials',
  'invalid api key',
  'invalid access token',
  'token expired',
  'token has expired',
  'expired access token',
  'expired credentials',
  'permission denied',
  'access denied',
  'forbidden',
] as const;

export interface McpToolDescriptor {
  name: string;
  inputSchema?: { required?: string[] } | null;
}

/**
 * True when a tool descriptor is provably side-effect-free: its name carries a
 * read-only verb token AND it declares no required input parameters, so calling
 * it with `{}` cannot mutate. This is the STRUCTURAL read-only guard — both the
 * heuristic pick and the operator-stored override (ENG-6212) must pass it, so a
 * stored slug can never escape the invariant (it only chooses *which* safe tool,
 * never whether the call is safe).
 */
export function isReadonlyToolDescriptor(t: McpToolDescriptor | undefined | null): boolean {
  if (!t?.name) return false;
  // Tokenized match (not substring): the read verb must be a STANDALONE token,
  // so a mutating name like TARGET_UPDATE (which contains "GET" as a substring)
  // is not misclassified as read-only (CodeRabbit PR #1963). Composio slugs are
  // underscore-delimited (e.g. GMAIL_GET_PROFILE → [GMAIL, GET, PROFILE]).
  const tokens = t.name.toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
  const hasReadVerb = tokens.some((tok) => (READONLY_VERB_TOKENS as readonly string[]).includes(tok));
  if (!hasReadVerb) return false;
  const required = t.inputSchema?.required ?? [];
  return !(Array.isArray(required) && required.length > 0);
}

/**
 * Pick a provably side-effect-free tool from a server's tool list: name carries
 * a read-only verb token AND no required input parameters. Returns the tool
 * name, or `null` when none qualifies.
 */
export function pickSafeReadonlyTool(tools: McpToolDescriptor[]): string | null {
  for (const t of tools) {
    if (isReadonlyToolDescriptor(t)) return t.name;
  }
  return null;
}

/** Why an operator-stored override tool was not used (ENG-6212). */
export type OverrideFallbackReason =
  /** Stored slug is not in the server's live tools/list (the slug drifted). */
  | 'seed-drift'
  /** Stored slug exists but isn't structurally read-only (a bad seed; CI should have caught it). */
  | 'seed-invalid';

export interface ResolvedProbeTool {
  /** The tool that will actually be called (override when valid, else heuristic pick, else null). */
  toolName: string | null;
  /** Args to call it with — the stored args only when the override itself is used, else `{}`. */
  args: Record<string, unknown>;
  /** Set when a stored override was requested but NOT used (caller logs reason=<this>). */
  fallback?: OverrideFallbackReason;
  /** The override slug that was requested, when one was. */
  requestedTool?: string;
}

/**
 * Resolve which tool the probe should call, given the live tool list and an
 * optional operator-stored override (ENG-6212). The override is honoured ONLY
 * when it is present in the live list AND structurally read-only; otherwise we
 * fall back to the heuristic pick and report why (`seed-drift` / `seed-invalid`)
 * so the caller can emit a distinct log line. Never runs a non-read-only tool.
 */
export function resolveProbeTool(
  tools: McpToolDescriptor[],
  override?: { tool?: string | null; args?: Record<string, unknown> | null },
): ResolvedProbeTool {
  const requested = override?.tool?.trim();
  if (!requested) {
    return { toolName: pickSafeReadonlyTool(tools), args: {} };
  }
  const match = tools.find((t) => t?.name === requested);
  if (!match) {
    return { toolName: pickSafeReadonlyTool(tools), args: {}, fallback: 'seed-drift', requestedTool: requested };
  }
  if (!isReadonlyToolDescriptor(match)) {
    return { toolName: pickSafeReadonlyTool(tools), args: {}, fallback: 'seed-invalid', requestedTool: requested };
  }
  return { toolName: requested, args: override?.args ?? {}, requestedTool: requested };
}

/** True when a tool-call error message indicates the account couldn't be resolved. */
export function isAccountResolutionError(message: string): boolean {
  const m = message.toLowerCase();
  return ACCOUNT_RESOLUTION_ERROR_PATTERNS.some((p) => m.includes(p));
}

/**
 * ENG-6328 — True when a tool-call failure indicates the upstream provider
 * rejected the connection's credential (a revoked/expired OAuth grant): an
 * explicit auth phrase, OR a structured 401/403 surfaced in Composio's failure
 * envelope (`statusCode` / `status_code` / `http.status` /
 * `mercury_last_http_status_code` / "401 Client Error"). Tolerant of JSON
 * escaping (`\"statusCode\":401`). Callers MUST only consult this on a failure
 * (a JSON-RPC error, `result.isError`, or a `successful:false` envelope) — a bare
 * "401"/"forbidden" inside a SUCCESSFUL read's data must never flip the verdict.
 */
export function isUpstreamAuthError(message: string): boolean {
  const m = message.toLowerCase();
  if (UPSTREAM_AUTH_ERROR_PATTERNS.some((p) => m.includes(p))) return true;
  if (/\b(401|403)\s+client error/.test(m)) return true;
  // A 4xx-auth status adjacent to a status field (escaping/quoting tolerant).
  return /(status(?:_?code)?|http_?status(?:_code)?|mercury_last_http_status_code)["'\\\s]*[:=]["'\\\s]*(401|403)\b/.test(m);
}

/**
 * ENG-6328 — True when a Composio tool RESULT payload reports its OWN failure
 * (`successful:false`), even though the MCP `tools/call` itself returned 200 with
 * no `isError` flag. Composio wraps an upstream provider error this way, so
 * without this check a 401-bearing result falls through to a green "resolved the
 * account". Tolerant of Composio's `successful`/`successfull` (sic) spellings and
 * of JSON escaping. Only an EXPLICIT `false` counts.
 */
export function isComposioFailureEnvelope(text: string): boolean {
  return /["'\\]*(successful|successfull)["'\\\s]*:\s*false\b/i.test(text);
}

/**
 * ENG-6328 — classify a tool-call FAILURE into the three verdict-bearing kinds.
 * Account-resolution is checked first (it's the Composio-side miss the probe was
 * built for); upstream-auth next (the dead-credential signal); everything else
 * is a benign tool error (bad args, validation) that still proves the account
 * resolved AND the credential was accepted.
 */
export function classifyToolCallFailure(text: string): 'account' | 'auth' | 'benign' {
  if (isAccountResolutionError(text)) return 'account';
  if (isUpstreamAuthError(text)) return 'auth';
  return 'benign';
}

/** Extract the JSON-RPC response for `expectedId` from a JSON or SSE body. */
async function parseRpc(res: Response, expectedId: number): Promise<Record<string, unknown> | null> {
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('text/event-stream')) {
    const text = await res.text();
    let dataLines: string[] = [];
    for (const rawLine of text.split(/\r?\n/)) {
      if (rawLine.startsWith('data:')) {
        dataLines.push(rawLine.slice(5).trimStart());
        continue;
      }
      if (rawLine === '' && dataLines.length > 0) {
        try {
          const msg = JSON.parse(dataLines.join('\n')) as Record<string, unknown>;
          if (('result' in msg || 'error' in msg) && msg['id'] === expectedId) return msg;
        } catch { /* skip malformed frame */ }
        dataLines = [];
      }
    }
    return null;
  }
  const msg = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  // Per JSON-RPC, a response is only valid if it carries result/error AND
  // matches the request id. A mismatched/malformed message must not leak to the
  // caller (it would let a wrong-id error/result drive the verdict) — return null
  // and let the caller treat it as "no confirmable failure" (Phase 1 governs).
  if (msg && ('result' in msg || 'error' in msg) && msg['id'] === expectedId) return msg;
  return null;
}

export interface ComposioToolCallProbeConfig {
  /** The agent's wired MCP URL (`…/v3/mcp/<serverId>/mcp?user_id=…`). */
  url: string;
  /** Headers from the wired server (carries `x-api-key`). */
  headers?: Record<string, string>;
  timeoutMs?: number;
  /**
   * ENG-6212 — operator-stored override: call THIS specific tool (e.g.
   * `GMAIL_GET_PROFILE`) instead of auto-picking. Honoured only when the slug is
   * present in the live tools/list AND structurally read-only; otherwise the
   * probe silently falls back to the heuristic pick and reports the reason via
   * `outcome.details.override_fallback` (caller logs reason=seed-drift/-invalid).
   */
  toolName?: string | null;
  /** Args for the override tool (default `{}`). Ignored unless the override is used. */
  toolArgs?: Record<string, unknown> | null;
}

/**
 * Probe the wired Composio MCP server with a safe read-only tool call. See the
 * module header for the outcome contract. Never throws.
 */
export async function probeComposioMcpToolCall(
  config: ComposioToolCallProbeConfig,
  fetchImpl: typeof fetch = fetch,
): Promise<ConnectivityProbeOutcome | null> {
  const timeoutMs = config.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const baseHeaders: Record<string, string> = {
    ...(config.headers ?? {}),
    'Content-Type': 'application/json',
    Accept: MCP_ACCEPT,
  };

  try {
    // 1. initialize
    const initRes = await fetchImpl(config.url, {
      method: 'POST',
      headers: baseHeaders,
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 1,
        method: 'initialize',
        params: {
          protocolVersion: '2025-03-26',
          capabilities: {},
          clientInfo: { name: 'augmented-toolcall-probe', version: '1.0.0' },
        },
      }),
      signal: AbortSignal.timeout(timeoutMs),
    });
    if (!initRes.ok) {
      return initRes.status >= 500
        ? { status: 'transient_error', message: `MCP initialize returned ${initRes.status}` }
        : null; // 4xx on handshake → can't run Phase 2; let Phase 1 govern.
    }
    const sessionId = initRes.headers.get('mcp-session-id');
    await parseRpc(initRes, 1);
    const sessionHeaders = { ...baseHeaders, ...(sessionId ? { 'Mcp-Session-Id': sessionId } : {}) };

    // 2. notifications/initialized
    const initializedRes = await fetchImpl(config.url, {
      method: 'POST',
      headers: sessionHeaders,
      body: JSON.stringify({ jsonrpc: '2.0', method: 'notifications/initialized' }),
      signal: AbortSignal.timeout(5_000),
    });
    await initializedRes.text().catch(() => '');

    // 3. tools/list — find a provably safe tool to call.
    const listRes = await fetchImpl(config.url, {
      method: 'POST',
      headers: sessionHeaders,
      body: JSON.stringify({ jsonrpc: '2.0', id: 2, method: 'tools/list' }),
      signal: AbortSignal.timeout(timeoutMs),
    });
    if (!listRes.ok) {
      return listRes.status >= 500
        ? { status: 'transient_error', message: `MCP tools/list returned ${listRes.status}` }
        : null;
    }
    const listRpc = await parseRpc(listRes, 2);
    const tools = ((listRpc?.['result'] as { tools?: McpToolDescriptor[] } | undefined)?.tools) ?? [];
    // ENG-6212: honour an operator-stored override when it's present in the
    // live list AND structurally read-only; otherwise fall back to the
    // heuristic pick and record why. resolveProbeTool never returns a tool that
    // isn't provably read-only, so the invariant holds for stored values too.
    const resolved = resolveProbeTool(tools, { tool: config.toolName, args: config.toolArgs });
    const toolName = resolved.toolName;
    if (!toolName) return null; // no safe tool — skip, Phase 1 governs.
    // Carried into every outcome so the caller can log reason=seed-drift/-invalid
    // and the UI can show which tool actually ran.
    const baseDetails: Record<string, unknown> = {
      tool: toolName,
      ...(resolved.fallback ? { override_fallback: resolved.fallback, requested_tool: resolved.requestedTool } : {}),
    };

    // 4. tools/call — read-only tool, stored args only when the override is used.
    const callRes = await fetchImpl(config.url, {
      method: 'POST',
      headers: sessionHeaders,
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 3,
        method: 'tools/call',
        params: { name: toolName, arguments: resolved.args },
      }),
      signal: AbortSignal.timeout(timeoutMs),
    });
    if (!callRes.ok) {
      return callRes.status >= 500
        ? { status: 'transient_error', message: `MCP tools/call returned ${callRes.status}` }
        : null;
    }
    const callRpc = await parseRpc(callRes, 3);

    // ENG-6212: capture a truncated raw response for the Test modal's
    // collapsed "technical details" — the read-only tool's own output (a
    // profile blob, an error string). Surfaced on every outcome below.
    // ENG-6224: cap at 2000 (was 600) so a typical profile JSON stays a
    // COMPLETE, parseable object for the modal's pretty-print + summary.
    {
      const errText = (callRpc?.['error'] as { message?: string } | undefined)?.message;
      const resContent = (callRpc?.['result'] as { content?: Array<{ text?: string }> } | undefined)?.content;
      const raw = errText ?? (resContent ?? []).map((c) => c.text ?? '').join(' ').trim();
      if (raw) baseDetails.response = raw.length > 2000 ? `${raw.slice(0, 2000)}…` : raw;
    }

    // A failure can arrive in any of three shapes:
    //   1. a JSON-RPC protocol error,
    //   2. a tool-level error (`result.isError` + content), or
    //   3. (ENG-6328) a Composio `successful:false` envelope embedded in the
    //      result content — Composio wraps an UPSTREAM provider failure (e.g. a
    //      Linear 401) as a SUCCESSFUL MCP call whose payload says
    //      `successful:false`, with NO isError flag. Pre-fix this fell through to
    //      a green "resolved the account", so a revoked OAuth grant read OK.
    const rpcErrMsg =
      callRpc && 'error' in callRpc
        ? (callRpc['error'] as { message?: string } | undefined)?.message ?? ''
        : '';
    const result = callRpc?.['result'] as { isError?: boolean; content?: Array<{ text?: string }> } | undefined;
    const contentText = (result?.content ?? []).map((c) => c.text ?? '').join(' ').trim();
    const failed = Boolean(rpcErrMsg) || Boolean(result?.isError) || isComposioFailureEnvelope(contentText);

    if (failed) {
      const failureText = [rpcErrMsg, contentText].filter(Boolean).join(' ');
      const snippet = failureText.length > 200 ? `${failureText.slice(0, 200)}…` : failureText;
      const kind = classifyToolCallFailure(failureText);
      if (kind === 'account') {
        return {
          status: 'down',
          message: `Live tool call '${toolName}' failed to resolve the connected account: ${snippet}`,
          details: baseDetails,
        };
      }
      if (kind === 'auth') {
        // ENG-6328: the wired server RESOLVED the account, but the upstream
        // provider rejected its credential (401/403, "authentication required").
        // Composio still reports the account ACTIVE with
        // auth_refresh_required:false, so the account + auth_config checks pass —
        // only this live call proves the OAuth grant is dead. Reconnect required.
        return {
          status: 'down',
          message:
            `Live tool call '${toolName}' was rejected by the provider — the connection's ` +
            `credential is no longer valid (reconnect required): ${snippet}`,
          details: { ...baseDetails, reason: 'upstream_auth_rejected' },
        };
      }
      // A benign tool error (bad args, validation) still proves the account
      // resolved AND the credential was accepted.
      return { status: 'ok', message: `Live tool call '${toolName}' resolved the account (tool error: ${snippet})`, details: baseDetails };
    }

    return { status: 'ok', message: `Live tool call '${toolName}' resolved the connected account`, details: baseDetails };
  } catch (err) {
    const isAbort = (err as Error)?.name === 'TimeoutError' || (err as Error)?.name === 'AbortError';
    return {
      status: 'transient_error',
      message: isAbort
        ? `MCP tool-call probe timed out after ${timeoutMs / 1000}s`
        : `MCP tool-call probe failed: ${(err as Error).message}`,
    };
  }
}
